import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {AlertTriangle, CheckCircle2, Calendar, ArrowRight, Bug, Shield, Heart, Syringe} from 'lucide-react';
import BookingModal from '../components/BookingModal';

const AfricaSafari = () => {
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const healthRisks = [
    {
      icon: <Bug className="w-6 h-6" />,
      title: "Malaria Risk",
      desc: "High risk in many safari destinations. Prophylaxis essential for most regions."
    },
    {
      icon: <Syringe className="w-6 h-6" />,
      title: "Yellow Fever Requirement",
      desc: "Mandatory vaccination for many countries. Proof required for entry and onward travel."
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Animal Exposure",
      desc: "Rabies risk from wildlife and domestic animals in rural areas."
    }
  ];

  const vaccinesDiscuss = [
    { name: "Yellow Fever", priority: "Required", note: "Mandatory for most sub-Saharan countries. Valid 10 days after vaccination." },
    { name: "Hepatitis A", priority: "Highly Recommended", note: "Protection against contaminated food and water" },
    { name: "Typhoid", priority: "Highly Recommended", note: "Essential for food/water safety" },
    { name: "Rabies (Pre-Exposure)", priority: "Recommended for Safari/Rural", note: "Important for wildlife exposure and remote areas" },
    { name: "Hepatitis B", priority: "Recommended", note: "For extended stays or medical exposure risk" },
    { name: "Meningococcal", priority: "Recommended for Meningitis Belt", note: "Required for travel during dry season (Dec-Jun) in certain regions" },
    { name: "Polio", priority: "Ensure Up-to-Date", note: "Booster may be required for some countries" },
    { name: "Measles/Mumps/Rubella (MMR)", priority: "Ensure Up-to-Date", note: "Measles outbreaks occur in the region" },
    { name: "Tetanus/Diphtheria/Pertussis", priority: "Ensure Up-to-Date", note: "Booster every 10 years" }
  ];

  const medications = [
    "Malaria prophylaxis (chloroquine, atovaquone-proguanil, doxycycline, or mefloquine – prescribed based on destination)",
    "Travellers' diarrhea treatment kit with antibiotics",
    "Oral rehydration salts",
    "High-strength insect repellent (DEET 30-50% or picaridin)",
    "Antimalarial mosquito nets if camping",
    "Broad-spectrum sunscreen and lip balm (SPF 50+)"
  ];

  const countries = [
    { name: "Kenya", highlights: ["Masai Mara", "Nairobi", "Coastal Areas"] },
    { name: "Tanzania", highlights: ["Serengeti", "Kilimanjaro", "Zanzibar"] },
    { name: "South Africa", highlights: ["Kruger", "Cape Town", "Garden Route"] },
    { name: "Botswana", highlights: ["Okavango Delta", "Chobe", "Kalahari"] },
    { name: "Uganda", highlights: ["Bwindi", "Queen Elizabeth NP", "Kampala"] },
    { name: "Rwanda", highlights: ["Volcanoes NP", "Kigali", "Nyungwe Forest"] },
    { name: "Zambia", highlights: ["South Luangwa", "Victoria Falls", "Kafue"] },
    { name: "Zimbabwe", highlights: ["Hwange", "Victoria Falls", "Mana Pools"] }
  ];

  const safetyTips = [
    {
      icon: <Bug className="w-5 h-5" />,
      tip: "Malaria Prevention",
      detail: "Take prophylaxis as prescribed. Use repellent and nets. Cover up at dusk/dawn."
    },
    {
      icon: <Shield className="w-5 h-5" />,
      tip: "Wildlife Safety",
      detail: "Maintain safe distance from animals. Never approach or feed wildlife."
    },
    {
      icon: <Heart className="w-5 h-5" />,
      tip: "Water & Food",
      detail: "Drink bottled or boiled water. Avoid raw foods and unpeeled fruits."
    },
    {
      icon: <AlertTriangle className="w-5 h-5" />,
      tip: "Sun Protection",
      detail: "African sun is intense. Wear hat, sunglasses, and reapply sunscreen frequently."
    }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-navy-900">
      {/* Hero */}
      <section className="relative py-20 bg-gradient-to-br from-yellow-600 via-orange-600 to-red-700 overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-10" />
        <div className="absolute inset-0">
          <img 
            src="https://images.pexels.com/photos/1670732/pexels-photo-1670732.jpeg?auto=compress&cs=tinysrgb&w=1920"
            alt="African Safari"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl"
          >
            <div className="inline-block px-4 py-2 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 text-white text-sm font-medium mb-6">
              Safari • Volunteering • Wildlife Conservation
            </div>
            <h1 className="text-4xl md:text-6xl font-heading font-bold text-white mb-6">
              Sub-Saharan Africa Safari & Volunteering
            </h1>
            <p className="text-xl text-white/90 mb-8 leading-relaxed">
              Comprehensive health preparation for your African adventure. From safari game drives to community volunteering, we'll help you stay protected in high-risk environments.
            </p>
            <button
              onClick={() => setIsBookingModalOpen(true)}
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-orange-600 text-lg font-bold rounded-full shadow-xl hover:shadow-2xl hover:scale-105 transition-all"
            >
              Book Africa Travel Consultation
              <ArrowRight size={20} />
            </button>
          </motion.div>
        </div>
      </section>

      {/* Countries Covered */}
      <section className="py-16 bg-white dark:bg-navy-900 border-b border-gray-200 dark:border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-2xl font-heading font-bold text-navy-900 dark:text-white mb-8 text-center">
            Popular Safari Destinations
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {countries.map((country, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="p-4 bg-gray-50 dark:bg-navy-800 rounded-xl border border-gray-100 dark:border-white/5 hover:border-orange-500/30 transition-all"
              >
                <h4 className="font-bold text-navy-900 dark:text-white mb-2">{country.name}</h4>
                <div className="flex flex-wrap gap-1">
                  {country.highlights.map((place, i) => (
                    <span key={i} className="text-xs text-gray-600 dark:text-gray-400">
                      {place}{i < country.highlights.length - 1 ? ' •' : ''}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Health Risks */}
      <section className="py-20 bg-gray-50 dark:bg-navy-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-navy-900 dark:text-white mb-4">
              Key Health Risks to Discuss
            </h2>
            <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Sub-Saharan Africa presents unique health challenges. Proper preparation is essential for safe travel.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {healthRisks.map((risk, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-6 bg-white dark:bg-navy-900 rounded-2xl shadow-lg border border-gray-100 dark:border-white/5"
              >
                <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/20 rounded-xl flex items-center justify-center text-orange-600 dark:text-orange-400 mb-4">
                  {risk.icon}
                </div>
                <h3 className="text-xl font-bold text-navy-900 dark:text-white mb-3">{risk.title}</h3>
                <p className="text-gray-600 dark:text-gray-400 leading-relaxed">{risk.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Vaccines & Medications */}
      <section className="py-20 bg-white dark:bg-navy-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Vaccines */}
            <div>
              <h2 className="text-3xl font-heading font-bold text-navy-900 dark:text-white mb-6">
                Vaccines to Discuss with Your Clinician
              </h2>
              <div className="space-y-4">
                {vaccinesDiscuss.map((vaccine, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="p-4 bg-gray-50 dark:bg-navy-800 rounded-xl border border-gray-100 dark:border-white/5 hover:border-cyan-500/30 transition-all"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-bold text-navy-900 dark:text-white">{vaccine.name}</h4>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ml-2 ${
                        vaccine.priority === "Required"
                          ? "bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400"
                          : vaccine.priority.includes("Highly")
                          ? "bg-orange-100 dark:bg-orange-900/20 text-orange-700 dark:text-orange-400"
                          : vaccine.priority.includes("Recommended")
                          ? "bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400"
                          : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                      }`}>
                        {vaccine.priority}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{vaccine.note}</p>
                  </motion.div>
                ))}
              </div>

              <div className="mt-6 p-4 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-xl">
                <div className="flex gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
                  <div className="text-sm text-red-900 dark:text-red-100">
                    <strong>Yellow Fever Certificate:</strong> Mandatory for most sub-Saharan countries. Must be administered at an authorized center. Valid 10 days after vaccination and lasts lifetime.
                  </div>
                </div>
              </div>
            </div>

            {/* Medications & Safety */}
            <div>
              <h2 className="text-3xl font-heading font-bold text-navy-900 dark:text-white mb-6">
                Medications & Malaria Prevention
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Essential medications and supplies for African safari travel:
              </p>
              <ul className="space-y-3 mb-8">
                {medications.map((med, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="flex items-start gap-3"
                  >
                    <CheckCircle2 className="w-5 h-5 text-orange-600 dark:text-orange-400 shrink-0 mt-0.5" />
                    <span className="text-gray-700 dark:text-gray-300">{med}</span>
                  </motion.li>
                ))}
              </ul>

              <h3 className="text-xl font-bold text-navy-900 dark:text-white mb-4">Safari Safety Tips</h3>
              <div className="space-y-3">
                {safetyTips.map((tip, index) => (
                  <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-navy-800 rounded-lg">
                    <div className="w-8 h-8 bg-cyan-100 dark:bg-cyan-900/20 rounded-lg flex items-center justify-center text-cyan-600 dark:text-cyan-400 shrink-0">
                      {tip.icon}
                    </div>
                    <div>
                      <div className="font-bold text-navy-900 dark:text-white text-sm">{tip.tip}</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">{tip.detail}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* When to Book */}
      <section className="py-20 bg-gradient-to-br from-navy-900 to-navy-800">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Calendar className="w-16 h-16 text-cyan-400 mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">
              When to Book Your Consultation
            </h2>
            <p className="text-gray-300 text-lg">
              African safari requires advance planning for comprehensive health protection.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <div className="p-6 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl text-center">
              <div className="text-4xl font-bold text-cyan-400 mb-2">8-12 Weeks</div>
              <div className="text-white font-medium mb-2">Highly Recommended</div>
              <div className="text-sm text-gray-400">Time for multi-dose vaccines (rabies, Hep B) and malaria prep</div>
            </div>
            <div className="p-6 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl text-center">
              <div className="text-4xl font-bold text-orange-400 mb-2">6 Weeks</div>
              <div className="text-white font-medium mb-2">Minimum Timeline</div>
              <div className="text-sm text-gray-400">Allows time for yellow fever and other essential vaccines</div>
            </div>
            <div className="p-6 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl text-center">
              <div className="text-4xl font-bold text-yellow-400 mb-2">10 Days</div>
              <div className="text-white font-medium mb-2">Yellow Fever Validity</div>
              <div className="text-sm text-gray-400">Yellow fever certificate valid 10 days after vaccination</div>
            </div>
          </div>

          <div className="text-center">
            <button
              onClick={() => setIsBookingModalOpen(true)}
              className="inline-flex items-center gap-2 px-10 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-lg font-bold rounded-full shadow-xl hover:shadow-2xl hover:scale-105 transition-all"
            >
              Book Your Africa Safari Consultation
              <ArrowRight size={20} />
            </button>
            <p className="text-gray-400 text-sm mt-4">
              Virtual consultations • $60 CAD • Malaria prophylaxis & yellow fever coordination
            </p>
          </div>
        </div>
      </section>

      {/* Booking Modal */}
      <BookingModal 
        isOpen={isBookingModalOpen} 
        onClose={() => setIsBookingModalOpen(false)} 
      />
    </div>
  );
};

export default AfricaSafari;
